import React, { useEffect, useState } from 'react';
import { 
  RefreshCw, 
  CheckCircle, 
  XCircle, 
  Clock,
  Filter,
  FileText
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { getWebhookLogs } from '../lib/api';
import { formatDateTime, cn } from '../lib/utils';
import { toast } from 'sonner';

export const WebhookLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [expandedLog, setExpandedLog] = useState(null);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const response = await getWebhookLogs();
      setLogs(response.data);
    } catch (error) {
      console.error('Error fetching logs:', error);
      toast.error('Erro ao carregar logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
    const interval = setInterval(fetchLogs, 10000); // Refresh every 10s
    return () => clearInterval(interval);
  }, []);

  const filteredLogs = logs.filter(log => {
    if (filter === 'all') return true;
    return log.status === filter;
  });

  const getStatusIcon = (status) => {
    switch (status) {
      case 'processed':
        return <CheckCircle className="text-green-400" size={16} />;
      case 'failed':
        return <XCircle className="text-red-400" size={16} />;
      case 'received':
        return <Clock className="text-yellow-400" size={16} />;
      default:
        return <Clock className="text-zinc-400" size={16} />;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      processed: 'bg-green-500/20 text-green-400 border-green-500/15',
      failed: 'bg-red-500/20 text-red-400 border-red-500/30',
      received: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/15',
    };
    const labels = {
      processed: 'Processado',
      failed: 'Falhou',
      received: 'Recebido',
    };
    return (
      <Badge variant="outline" className={cn('text-xs', styles[status])}>
        {labels[status] || status}
      </Badge>
    );
  };

  const getEventTypeBadge = (eventType) => {
    const colors = {
      'message.received': 'bg-blue-500/20 text-blue-400',
      'message.sent': 'bg-green-500/20 text-green-400',
      'message.status': 'bg-purple-500/20 text-purple-400',
      'connection.qrcode': 'bg-yellow-500/20 text-yellow-400',
      'connection.connected': 'bg-green-500/20 text-green-400',
      'connection.disconnected': 'bg-red-500/20 text-red-400',
      'connection.expired': 'bg-orange-500/20 text-orange-400',
    };
    return (
      <Badge className={cn('text-xs font-mono', colors[eventType] || 'bg-zinc-500/20 text-zinc-400')}>
        {eventType}
      </Badge>
    );
  };

  return (
    <div className="flex-1 overflow-auto p-6" data-testid="webhook-logs-page">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold font-mono">Logs de Webhook</h1>
            <p className="text-muted-foreground mt-1">
              Histórico de eventos recebidos via webhook
            </p>
          </div>
          <div className="flex gap-2">
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-40 bg-secondary border-0">
                <Filter size={14} className="mr-2" />
                <SelectValue placeholder="Filtrar" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="processed">Processados</SelectItem>
                <SelectItem value="received">Recebidos</SelectItem>
                <SelectItem value="failed">Falhas</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchLogs} disabled={loading} data-testid="refresh-logs-btn">
              <RefreshCw size={16} className={cn('mr-2', loading && 'animate-spin')} />
              Atualizar
            </Button>
          </div>
        </div>

        <Card className="bg-card border-border">
          <CardHeader className="pb-3">
            <CardTitle className="font-mono text-lg flex items-center gap-2">
              <FileText size={18} />
              Eventos ({filteredLogs.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[600px]">
              {filteredLogs.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Nenhum log encontrado</p>
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {filteredLogs.map((log) => (
                    <div 
                      key={log.id}
                      className="p-4 hover:bg-white/5 cursor-pointer transition-colors"
                      onClick={() => setExpandedLog(expandedLog === log.id ? null : log.id)}
                      data-testid={`log-${log.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {getStatusIcon(log.status)}
                          <div>
                            <div className="flex items-center gap-2">
                              {getEventTypeBadge(log.event_type)}
                              {getStatusBadge(log.status)}
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">
                              {formatDateTime(log.timestamp)}
                            </p>
                          </div>
                        </div>
                        <span className="text-xs text-muted-foreground font-mono">
                          {log.id.substring(0, 8)}...
                        </span>
                      </div>
                      
                      {log.error_message && (
                        <p className="text-sm text-destructive mt-2 pl-7">
                          {log.error_message}
                        </p>
                      )}
                      
                      {expandedLog === log.id && (
                        <div className="mt-3 pl-7">
                          <p className="text-xs text-muted-foreground mb-1">Payload:</p>
                          <pre className="p-3 bg-secondary rounded text-xs overflow-auto max-h-48">
                            {JSON.stringify(log.payload, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default WebhookLogs;
